import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Location } from "history";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { TableChangeParams } from "../../../../components/Table/types";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import { EnumPaths } from "../../../../utils/navigation";
import history from "../../../../history";
import Table from "../../../../components/Table";
import Dropdown, {
  DropdownValues,
} from "../../../../components/Tailwind/Dropdown";
import images from "../../../../images";
import { getTranslation } from "../../../../utils/translation";
import ConfirmModal from "../../../../utils/modal/confirm";
import IntlMessages from "../../../../utils/messages";
import {
  displayDayDDMMYYYY_HHMM,
  DEFAULT_LIMIT,
} from "../../../../utils/config";
import { QuoteRequestStatus } from "../../../../lib/ground-aws-graphql-core/models/Api/QuoteRequests";

interface Props {
  intl: IntlShape;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
  }>;
}

const ListQuoteRequestStatus = (props: Props): JSX.Element => {
  const { intl, location } = props;
  const [loading, setLoading] = useState(false);

  const [
    showDeleteQuoteRequestStatusModal,
    setShowDeleteQuoteRequestStatusModal,
  ] = useState(false);

  const [quoteRequestStatusId, setQuoteRequestStatusId] = useState<number>();
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.listQuoteRequestStatus
  );

  const quoteRequestStatuss = GroundGraphqlContextStore.useStoreState(
    actions => actions.quoteRequests.quoteRequestStatuss.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    actions => actions.quoteRequests.quoteRequestStatuss.total
  );

  const setQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.setQuoteRequestStatus
  );

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const deleteQuoteRequestStatus = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.deleteQuoteRequestStatus
  );

  useEffect(() => {
    if (location?.state) {
      fetchData(location?.state?.pageIndex, true, location?.state?.limit);
    } else fetchData();
  }, []);

  const fetchData = (pageIndex = 0, loader = true, limit = DEFAULT_LIMIT) => {
    if (loader) setLoading(true);

    listQuoteRequestStatus({
      offset: pageIndex * limit,
      limit,
    }).finally(() => setLoading(false));
  };

  const addNewQuoteRequestStatus = () => {
    history.push(
      `/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_STATUS}/${EnumPaths.CREATION_MODE}`
    );
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit } = changeParams;
    setInitialParams(changeParams);
    fetchData(pageIndex, false, limit);
  };

  const handleEditQuoteRequestStatus = (id: number) => {
    const editLink = `/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_STATUS}/${id}/${EnumPaths.EDIT_MODE}`;
    setQuoteRequestStatus(null);
    history.push(editLink);
  };

  const handleDelete = () => {
    setLoading(true);
    setShowDeleteQuoteRequestStatusModal(false);
    deleteQuoteRequestStatus({ id: quoteRequestStatusId! })
      .then(() => {
        toast(
          intl.formatMessage({
            id: "page.quote.request.status.delete.success",
          }),
          { type: "success" }
        );
      })
      .catch(() => {
        toast(
          intl.formatMessage({
            id: "page.quote.request.status.delete.error",
          }),
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const tableHead = [
    "page.quote.request.status.label",
    "page.quote.request.status.code",
    "page.quote.request.status.created_at",
    "page.quote.request.status.updated_at",
    "general.actions",
  ];

  const tableBody = quoteRequestStatuss.map(
    (quoteRequestStatus: QuoteRequestStatus) => {
      const { id, code, label, created_at, updated_at } = quoteRequestStatus;
      const editLink = `/${EnumPaths.ROOT}/${EnumPaths.QUOTE_REQUEST_STATUS}/${id}/${EnumPaths.EDIT_MODE}`;

      const dropdownValues: DropdownValues[] = [
        {
          id: "edit_quote_request_status_key",
          label: "page.list.quote.request.status.edit",
          icon: images.edit,
          link: editLink,
        },
        {
          id: "delete",
          label: "general.delete",
          icon: images.deleteIcon,
          onClick: () => {
            setShowDeleteQuoteRequestStatusModal(
              !showDeleteQuoteRequestStatusModal
            );
            setQuoteRequestStatusId(id);
          },
        },
      ];

      const elements = [
        {
          element: getTranslation(label),
          onCellClick: () => handleEditQuoteRequestStatus(id),
        },
        {
          element: code,
          onCellClick: () => handleEditQuoteRequestStatus(id),
        },
        {
          element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
          onCellClick: () => handleEditQuoteRequestStatus(id),
        },
        {
          element: displayDayDDMMYYYY_HHMM(updated_at, centerTimezone),
          onCellClick: () => handleEditQuoteRequestStatus(id),
        },
        {
          element: (
            <Dropdown
              values={dropdownValues}
              dataCy={`quote-request-status-dropdown-${id}`}
            />
          ),
        },
      ];

      return {
        id: quoteRequestStatus.id.toString(),
        rowElements: elements,
      };
    }
  );

  return (
    <div>
      <Helmet>
        <title>
          {intl.formatMessage({
            id: `page.list.quote.request.status.document.title`,
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.quote.request.status.title"
        nbOfResults={total}
        buttonRightTitle="page.list.quote.request.status.create.quote.request.status"
        buttonRightAction={addNewQuoteRequestStatus}
        buttonRightId="btn-create-quote-request-status"
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        noDataText="page.list.quote.request.status.empty"
        onChange={handleOnChange}
        paginationLimit={DEFAULT_LIMIT}
        paginationTotal={total}
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />

      <ConfirmModal
        isOpen={showDeleteQuoteRequestStatusModal}
        onRequestClose={() =>
          setShowDeleteQuoteRequestStatusModal(
            !showDeleteQuoteRequestStatusModal
          )
        }
        toggle={() =>
          setShowDeleteQuoteRequestStatusModal(
            !showDeleteQuoteRequestStatusModal
          )
        }
        handleConfirm={handleDelete}
        content={<IntlMessages id="page.list.quote.request.status.delete" />}
      />
    </div>
  );
};

export default injectIntl(ListQuoteRequestStatus);
